import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '../../../utils/format';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

/**
 * AssessmentAlert
 * Directive Art. 10 triggered joint pay assessments and other flagged items.
 */

const priorityConfig = {
  critical: {
    wrapper: 'border-destructive/30 bg-destructive/[0.03]',
    pillBg: 'bg-destructive text-destructive-foreground',
    iconBg: 'bg-destructive/10 text-destructive',
    icon: 'AlertTriangle',
    labelKey: 'dashboard.assessmentAlert.priority.critical',
  },
  high: {
    wrapper: 'border-warning/30 bg-warning/[0.03]',
    pillBg: 'bg-warning text-warning-foreground',
    iconBg: 'bg-warning/10 text-warning',
    icon: 'Clock',
    labelKey: 'dashboard.assessmentAlert.priority.high',
  },
  medium: {
    wrapper: 'border-border',
    pillBg: 'bg-slate-600 text-white',
    iconBg: 'bg-slate-100 text-slate-700',
    icon: 'Info',
    labelKey: 'dashboard.assessmentAlert.priority.medium',
  },
  low: {
    wrapper: 'border-border',
    pillBg: 'bg-slate-100 text-slate-700',
    iconBg: 'bg-slate-50 text-slate-500',
    icon: 'Circle',
    labelKey: 'dashboard.assessmentAlert.priority.low',
  },
};

const statusLabel = (status, t) => {
  const map = {
    pending: t('common.pending'),
    in_progress: t('common.inProgress'),
    scheduled: t('common.scheduled'),
    completed: t('common.completed'),
  };
  return map[status] || status;
};

// formatDate replaced with locale-aware fmtDate from useFormatters

const AssessmentAlert = ({ alerts }) => {
  const { t } = useTranslation();
  const { date: fmtDate } = useFormatters();
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden h-full flex flex-col">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="text-sm font-semibold text-foreground">{t('dashboard.assessmentAlert.title')}</h3>
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase tracking-wide">
              Art. 10
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {alerts.length === 0 ? t('dashboard.assessmentAlert.noTriggered') : t('dashboard.assessmentAlert.requiringAction', { count: alerts.length })}
          </p>
        </div>
        <Link
          to="/joint-assessments"
          className="text-xs font-medium text-primary-solid hover:underline"
        >
          {t('common.viewAll')} →
        </Link>
      </div>

      <div className="flex-1 overflow-y-auto">
        {alerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-5">
            <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mb-3">
              <Icon name="CheckCircle2" size={24} className="text-success" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">{t('dashboard.assessmentAlert.allClear')}</p>
            <p className="text-xs text-muted-foreground text-center max-w-[220px]">
              {t('dashboard.assessmentAlert.allClearBody')}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {alerts.map((alert) => {
              const cfg = priorityConfig[alert.priority] || priorityConfig.medium;
              return (
                <div key={alert.id} className={`p-4 ${cfg.wrapper} border-l-2 ${
                  alert.priority === 'critical' ? 'border-l-destructive'
                  : alert.priority === 'high' ? 'border-l-warning'
                  : 'border-l-transparent'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className={`shrink-0 p-1.5 rounded-md ${cfg.iconBg}`}>
                      <Icon name={cfg.icon} size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-foreground leading-snug">
                          {alert.title}
                        </h4>
                        <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${cfg.pillBg}`}>
                          {t(cfg.labelKey)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed mb-2.5">
                        {alert.description}
                      </p>
                      <div className="flex items-center gap-3 flex-wrap text-[11px] text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Icon name="TrendingUp" size={10} />
                          <span className="font-semibold text-foreground tabular-nums">
                            {alert.gapPercentage}%
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Icon name="Calendar" size={10} />
                          <span>{t('dashboard.assessmentAlert.due', { date: fmtDate(alert.dueDate) })}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="w-1 h-1 rounded-full bg-muted-foreground" />
                          <span>{statusLabel(alert.status, t)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentAlert;
